import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { ReactComponent as LinkTitleIcon }  from '../../../assets/project-link-regular.svg';
import { getBulkEditFinopsinputProgramProjectOption, saveSearchedQuery } from 'service/service';
import AsyncAutoComplete from 'components/Inputs/ResourceMapping/AsyncAutocomplete';
import { useNavigate } from 'react-router-dom';
import { formatQuery } from 'react-querybuilder';
import { queryPrefOptions } from './QuerySearchHistory';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface ProjectAssociationModalProps {
    setIsProjectAssocModalOpen: any;
	query: any;
	setQuery: any;
	setIsReapplied: any;
	queryPreference: any;
    selectedCheckbox: any;
	selGroupPaths: any;
}

interface FormData {
	PepwiseProgram: string;
	PepwiseProject: string;
}

interface Option {
	value: string;
	label: string;
}

const ProjectAssociationModal: React.FC<ProjectAssociationModalProps> = ({ 
    setIsProjectAssocModalOpen, 
    query, 
    setQuery, 
    setIsReapplied, 
    queryPreference, 
    selectedCheckbox, 
    selGroupPaths}: any) => {
	const theme= useTheme();
	const colors = theme.palette;
	const [isProjectDisabled, setIsProjectDisabled] = useState(true);
	const [selectedProgramId, setSelectedProgramId] = useState<number>();
	const [selectedProjectId, setSelectedProjectId] = useState<number>();
	const [programOptions, setProgramOptions] = useState<Option[]>([]);
	const [projectOptions, setProjectOptions] = useState<Option[]>([]);
	const [projectsByProgram, setProjectsByProgram] = useState<Record<string, Option[]>>({});
    const [formattedQuery, setFormattedQuery] = useState<any>('');
    const navigate = useNavigate();
	const initialFormData: FormData = {
		PepwiseProgram: '',
		PepwiseProject: '',
	};
	const [formData, setFormData] = useState<FormData>(initialFormData);

	const commonAutocompleteComponentsProps = {
		paper: {
			sx: {
				borderRadius: '4px',
				backgroundColor: `${colors.DropdownV2.menuItemBackground} !important`,
				color: `${colors.DropdownV2.menuItemText} !important`,

				'&& .MuiAutocomplete-listbox': {
					paddingTop: '0 !important',
					paddingBottom: '0 !important',
				},

				'&& .MuiAutocomplete-option': {
					fontSize: '14px !important',
					fontFamily: 'Montserrat !important',
					color: `${colors.DropdownV2.menuItemText} !important`,
					backgroundColor: 'transparent !important',

					'& .MuiTypography-root': {
						color: `${colors.DropdownV2.menuItemText} !important`,
						fontSize: '14px !important',
						fontFamily: 'Montserrat !important',
						fontWeight: 'inherit !important',
					},
				},

				'&& .MuiAutocomplete-option:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},

				'&& .MuiAutocomplete-option.Mui-selected:hover': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
				},

				'&& .MuiAutocomplete-option.Mui-selected.Mui-focused .MuiTypography-root': {
					backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
					color: `${colors.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
				},
			},
		},
	};

	const commonAutocompleteSx = {
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.DropdownV2.borderColor} !important`,
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.DropdownV2.borderColorHover} !important`,
		},
		'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: `${colors.DropdownV2.borderColorHover} !important`,
		},
		'& .MuiAutocomplete-popupIndicator': {
			color: `${colors.DropdownV2.dropdownIconDefault} !important`,
			'& svg': {
				fill: `${colors.DropdownV2.dropdownIconDefault} !important`,
				color: `${colors.DropdownV2.dropdownIconDefault} !important`,
			},
		},
		'& .MuiAutocomplete-clearIndicator': {
			color: `${colors.DropdownV2.menuItemText} !important`,
			'& svg': {
				fill: `${colors.DropdownV2.menuItemText} !important`,
				color: `${colors.DropdownV2.menuItemText} !important`,
			},
		},
	};

	const commonAutocompleteTextFieldProps = {
		InputLabelProps: {
			sx: {
				'&&': { color: `${colors.DropdownV2.menuItemText} !important`, },
				'&.Mui-focused': { color: `${colors.DropdownV2.menuItemText} !important`, },
				'&.MuiInputLabel-shrink': { color: `${colors.DropdownV2.menuItemText} !important`, },
			},
		},
		InputProps: {
			sx: {
				'&&': { color: `${colors.DropdownV2.menuItemText} !important`, },
				'& input': { color: `${colors.DropdownV2.menuItemText} !important`, },
			},
		},
		sx: {
			'& .MuiOutlinedInput-root': {
				'& fieldset': { borderColor: `${colors.DropdownV2.borderColor} !important`, },
				'&:hover fieldset': { borderColor: `${colors.DropdownV2.borderColorHover} !important` },
				'&.Mui-focused fieldset': { borderColor: `${colors.DropdownV2.borderColorHover} !important` },
			},
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: `${colors.DropdownV2.borderColor} !important`,
			},
			'&:hover .MuiOutlinedInput-notchedOutline': {
				borderColor: `${colors.DropdownV2.borderColorHover} !important`
			},
			'& .Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: `${colors.DropdownV2.borderColorHover} !important`
			},
		},
	};

	const handleChange = (field: keyof FormData, value: string) => {
		setFormData((prevState) => {
			return {
			...prevState,
			[field]: value,
			};
		});
	};

	// Fetch program and project options
	const getInputProgramProjectOptions = async (input: string) => {
		try {
			const fieldsRes = await getBulkEditFinopsinputProgramProjectOption({ searchText: input });
			const programs = fieldsRes.data.programs;

			const programOptions: Option[] = programs.map((program: any) => ({
				value: program.programId,
				label: program.programName
			}));

			// Map `projects` for each program into a nested structure
			const projects: Record<string, Option[]> = {};
			programs.forEach((program: any) => {
				projects[program.programName] = program.projects.map((project: any) => ({
					value: project.projectId,
					label: project.projectName
				}));
			});

			return { programOptions, projects };
		} catch (error) {
			console.error('Error fetching program/project options:', error);
			return { programOptions: [], projects: {} };
		}
	};

	const getProgramProjectOptions = async (input: string | undefined, type: 'project' | 'program'): Promise<Option[]> =>{
		const { programOptions, projects } = await getInputProgramProjectOptions(input?? "");
		programOptions && setProgramOptions(programOptions);
		projects && setProjectsByProgram(projects);
		if(type === "program") return programOptions;
		return projects[`${formData.PepwiseProgram}`] ?? [];
	}

    const setQueryValue = () => {
        const filteredRules = query.rules.map((rule: { field: string; operator: string; value: string; id: string }) => {
            if (rule.field === "") {
                return { field: "1", operator: "=", value: "1", id: rule.id };
            }
            return rule;
        });
        const values = formatQuery({ ...query, rules: filteredRules }, 'parameterized');
        setFormattedQuery(values);
    }
    
    function getQueryPrefLabel(queryPreference: string) {
        const match = queryPrefOptions.find(option => option.value === queryPreference);
        return match ? match.label : '';
    };

	const handleLinkToProjectClick = async (event: any) => {
		try {
			localStorage.setItem(`query-preference`, JSON.stringify(queryPreference));
            const fieldsStr: any = localStorage.getItem(`resource-list_user_col_pref`);
            const fields = fieldsStr ? JSON.parse(fieldsStr) : [];
            const queryPrefVal = getQueryPrefLabel(queryPreference);
            const formPayload = {
                queryName: `${formData.PepwiseProgram}|${formData.PepwiseProject}|${queryPrefVal}`,
                query: formattedQuery,
                queryJson: query,
                selectedColumns: fields,
                queryPreference: queryPreference,
                selectedPaths: selectedCheckbox,
                groupPaths: selGroupPaths,
                program: selectedProgramId,
                project: selectedProjectId,
				isAllocated: 0,
            };
            const result = await saveSearchedQuery(formPayload);
            if(result.success) {
                setIsProjectAssocModalOpen(false);
                navigate(`/finops/resourcemapping/projects/${selectedProgramId}/${selectedProjectId}/
					${encodeURIComponent(formData.PepwiseProgram)}/${encodeURIComponent(formData.PepwiseProject)}`,
					{ state: { autoLoadOnMount: true } }
				);
			}
		} catch (err) {
			console.error(err);
		}
	};

	//fetching data
	useEffect(() => {
		localStorage.removeItem(`query-preference`);
		const fetchOptions = async () => {
			const { programOptions, projects } = await getInputProgramProjectOptions('');
			setProgramOptions(programOptions);
			setProjectsByProgram(projects);
		};
		fetchOptions();
		setQueryValue();
	}, []);

	// Update project options when a program is selected
	useEffect(() => {
		if (!!formData.PepwiseProgram) {
			setProjectOptions(projectsByProgram[`${formData.PepwiseProgram}`] || []);
			setIsProjectDisabled(false);
			setFormData((prevState) => {
				return {
					...prevState,
					PepwiseProject: '',
				};
			});
		} else {
			setFormData(initialFormData);
			setProjectOptions([]);
			setIsProjectDisabled(true);
			setFormData((prevState) => {
				return {
					...prevState,
					PepwiseProject: '',
				};
			});
		}
	}, [formData.PepwiseProgram]);
	
	return (
		<Stack direction="column" p={4} position='relative' sx={{ backgroundColor: `${colors.overlay.background} !important`, color: `${colors.overlay.title} !important`, height: '100%' }}>
			<Stack minWidth='250px'
				direction="row"
				spacing={1}
				pb={2}
				marginBottom={2}
				justifyContent="space-between"
				borderBottom={`0.5px solid ${colors.overlay.border} !important`}
			>
				<Stack direction="row" alignItems="flex-start" spacing={1}
					sx={{
						position: 'absolute',
						top: 15,
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<LinkTitleIcon className='close-icon' style={{ width: '24px', height: '24px', color: `${colors.overlay.IconColor} !important` }}/>
					<Typography fontSize="16px" sx={{ color: `${colors.overlay.title} !important`, fontWeight: 500 }}>Link Query To Project</Typography>
				</Stack>
				<IconButton 
					onClick={() => setIsProjectAssocModalOpen(false)} // Close drawer
					sx={{
						position: 'absolute',
						top: 8,
						right: 6,
						color: `${colors.overlay.IconColor} !important`,
						'& svg': {
							color: `${colors.overlay.IconColor} !important`,
							fill: `${colors.overlay.IconColor} !important`
						},
						'& svg path': {
							fill: `${colors.overlay.IconColor} !important`
						}
					}}
				>
					<CloseIcon sx={{ color: `${colors.overlay.IconColor} !important`, fill: `${colors.overlay.IconColor} !important` }}/>
				</IconButton>
			</Stack>
			<Stack direction="column"
				spacing={1.5}
				pb={2}
				marginBottom={1}
				justifyContent="space-between">
				<AsyncAutoComplete
					textFieldLabel={'Select Program'}
					inputValue={formData['PepwiseProgram']}
					value={formData['PepwiseProgram']}
					onChange={(event, value) => {
						const selectedProgram = programOptions.find((option) => option.label === value);
						const programId = selectedProgram && Number(selectedProgram.value);

						programId && setSelectedProgramId(programId);
						handleChange('PepwiseProgram', value);
					}}
					mappingType=""
					columnName={'PepwiseProgram'}
					options={programOptions}
					popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
					sx={commonAutocompleteSx}
					componentsProps={commonAutocompleteComponentsProps}
					customTextFieldProps={commonAutocompleteTextFieldProps}
					fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'program')}
				/>
				<AsyncAutoComplete
					textFieldLabel={'Select Project'}
					inputValue={formData['PepwiseProject']}
					value={formData['PepwiseProject']}
					onChange={(event, value) => {
						const selectedProject = projectOptions.find((option) => option.label === value);
						const projectId = selectedProject && Number(selectedProject.value);

						projectId && setSelectedProjectId(projectId);
						handleChange('PepwiseProject', value);
					}}
					mappingType=""
					columnName={'PepwiseProject'}
					options={projectOptions}
					disabled={isProjectDisabled}
					popupIcon={<KeyboardArrowDownIcon sx={{ color: colors.DropdownV2.dropdownIconDefault }} />}
					sx={commonAutocompleteSx}
					componentsProps={commonAutocompleteComponentsProps}
					customTextFieldProps={commonAutocompleteTextFieldProps}
					// fieldValueOptionsCallback={async (input) => await getProgramProjectOptions(input, 'project')}
				/>
				<Stack direction="row">
                    <Button variant="pepwiseSecondary" 
                    disabled = {!formData.PepwiseProgram || !formData.PepwiseProject} 
                    onClick={handleLinkToProjectClick} sx={{ flex: 1, textTransform: 'uppercase' }}>
                        Link To Project
                    </Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default ProjectAssociationModal;
